import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { signOut, useSession } from '@ghq-abi/auth-client-lib/client';
import { useTranslate } from '@tolgee/react';
import { RoleEnum } from 'abi-oneverse-common-types';
import nProgress from 'nprogress';

import { useGetProfilePicture } from '~/entities/Employee/hooks/useGetProfilePicture';
import { Icon } from '~/shared/components';
import { SignOut } from '~/shared/components/icons/SignOut';
import { canShowAdminEntry, hasMasterAdminRole } from '~/shared/utils/adminPanel';

export const useUserDropdown = () => {
    const [profileInfo, setProfileInfo] = useState({
        name: '',
        urlImage: '',
        zone: '',
    });
    const { t } = useTranslate();
    const session = useSession();
    const router = useRouter();

    const user = session.data?.user.proxiedAs ?? session.data?.user;
    const userCanProxy: boolean =
        user?.oneVerseRoles
            ?.map(({ role }: { role: RoleEnum }) => role)
            .includes(RoleEnum.CAN_PROXY) ?? false;
    const isMasterAdmin = hasMasterAdminRole(user?.oneVerseRoles);
    const isProxying = session.data?.user?.proxiedAs !== null;

    const { data } = useGetProfilePicture(user?.globalId ?? '');

    async function handleSignOut() {
        signOut({ redirect: false });
    }

    const goToProxy = useCallback(async () => {
        router.push('/proxy');
    }, [router]);

    const goToAdmin = useCallback(async () => {
        router.push('/admin');
    }, [router]);

    const handleStopProxy = useCallback(async () => {
        if (!session) {
            return;
        }

        nProgress.start();

        await session.proxyOut();

        nProgress.done();
    }, [session]);

    const items = useMemo(() => {
        const dropdownItems = [
            {
                label: t('common.sign_out'),
                onSelect: handleSignOut,
                icon: <SignOut />,
            },
            userCanProxy &&
                !isProxying && {
                    label: t('proxy.proxy_as_user'),
                    onSelect: goToProxy,
                    icon: <Icon.ProxyIcon size={18} />,
                },
            canShowAdminEntry({ hasAdminAccess: isMasterAdmin }) && {
                label: t('common.navbar.admin'),
                onSelect: goToAdmin,
                icon: <Icon.Admin size={18} />,
            },
            isProxying && {
                label: t('proxy.stop_proxy'),
                onSelect: handleStopProxy,
                icon: <Icon.StopProxy size={18} />,
            },
        ];

        return dropdownItems.filter(Boolean);
    }, [t, isProxying, handleStopProxy, goToAdmin, goToProxy, isMasterAdmin, userCanProxy]);

    useEffect(() => {
        setProfileInfo({
            name: user?.name ?? '',
            urlImage: data ? URL.createObjectURL(data) : 'undefined',
            zone: user?.zone ?? '',
        });
    }, [user, data]);

    return {
        items,
        profileInfo,
    };
};
